import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Scrollbars } from "react-custom-scrollbars";
import {
    useHistory,
    useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { FaUserTie } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../redux/user/actions";
import { usePermission } from "../../../hooks/usePermission";

// Fallback sidebar items in case backend hasn't provided them yet
const FALLBACK_MENU = [
    { path: "/admin", label: "Dashboard", module: "dashboard", exact: true },
    { path: "/admin/user", label: "User", module: "user" },
    { path: "/admin/edd", label: "EDD", module: "user" },
    { path: "/admin/alert", label: "Alert User", module: "user" },
    { path: "/admin/coin", label: "Coins", module: "coin" },
    {
        path: "/admin/tradingPair",
        label: "Trading Pair",
        module: "trading-pair",
    },
    { path: "/admin/wallet", label: "Wallet Transaction", module: "wallet" },
    { path: "/admin/order", label: "Order", module: "order" },
    { path: "/admin/withdrawal", label: "Withdrawal", module: "coin-buy-sell" },
    { path: "/admin/deposit", label: "Deposit", module: "coin-buy-sell" },
    { path: "/admin/faq", label: "FAQ's", module: "home-faq" },
    { path: "/admin/policy-link", label: "Policy Links", module: "policy-link" },
    { path: "/admin/newsletter", label: "Newsletter", module: "news-letter" },
    { path: "/admin/contact-us", label: "Contact Us", module: "contact-us" },
    { path: "/admin/bulk-trade-request", label: "Bulk Trade Requests", module: "bulk-trade-request" },
    {
        path: "/admin/third-party-stats",
        label: "Third Party Stats",
        module: "user",
    },
    {
        path: "/admin/balance-comparison",
        label: "Balance Comparison",
        module: "wallet",
    },
    {
        path: "/admin/coindcx-orders",
        label: "Exchange Trades",
        module: "order-transaction",
    },
    {
        path: "/admin/wallet-review",
        label: "Held Transactions",
        module: "wallet-tx-review",
    },
    {
        path: "/admin/network",
        label: "Coin Networks",
        module: "coin-network",
    },
    {
        path: "/admin/fireblocks-sync",
        label: "Fireblocks Sync",
        module: "fireblocks-config",
    },
    {
        path: "/admin/compliance",
        label: "Compliance Config",
        module: "compliance-config",
    },
    { path: "/admin/logs", label: "Logs", module: "logs" },
    // { path: "/admin/roles", label: "Role Management", module: "role" },
    // { path: "/admin/admin-users", label: "Admin Users", module: "role" },
];

const SidebarNav = () => {
    // Navigation has moved to the top navbar (Header component).
    return null;

    // eslint-disable-next-line no-unreachable
    const location = useLocation();
    const pathname = location.pathname;
    const dispatch = useDispatch();
    const history = useHistory();
    const { canView, isSuperAdmin } = usePermission();
    const { sidebarItems } = useSelector((state) => state.user);

    // Use backend sidebar items if available, otherwise fallback. We also
    // append any FALLBACK_MENU items the backend session hasn't sent yet —
    // that way newly-added sidebar entries appear for already-logged-in
    // users without forcing a re-login.
    const menuItems =
        sidebarItems && sidebarItems.length > 0
            ? [
                  ...sidebarItems,
                  ...FALLBACK_MENU.filter(
                      (f) => !sidebarItems.some((s) => s.path === f.path),
                  ),
              ]
            : FALLBACK_MENU;

    // eslint-disable-next-line no-unused-vars
    const [isSidebarExpanded, setSidebarExpanded] = useState(false);
    const [isMouseOverSidebar, setMouseOverSidebar] = useState(false);

    const logOut = () => {
        localStorage.clear();
        dispatch(actions.setLoggedIn(false));
        dispatch(actions.setUser(null));
        dispatch(actions.setToken(null));
        dispatch(actions.setRole(null));
        dispatch(actions.setPermission([]));
        dispatch(actions.setSidebarItems([]));
        history.push("/");
    };
    useEffect(() => {
        if (
            isMouseOverSidebar &&
            document.body.classList.contains("mini-sidebar")
        ) {
            document.body.classList.add("expand-menu");
            return;
        }
        document.body.classList.remove("expand-menu");
    }, [isMouseOverSidebar]);

    const handleMouseEnter = () => {
        setMouseOverSidebar(true);
    };

    const handleMouseLeave = () => {
        setMouseOverSidebar(false);
    };

    const visibleMenuItems = menuItems.filter(
        (item) => isSuperAdmin || canView(item.module),
    );

    const isActive = (item) => {
        if (item.exact) {
            return pathname === item.path;
        }
        // If the item path has a querystring, match on full path + search so
        // /admin/wallet?displayStatus=ON_HOLD does not light up alongside
        // /admin/wallet.
        if (item.path && item.path.includes("?")) {
            const [itemPath, itemSearch] = item.path.split("?");
            const currentSearch = (location.search || "").replace(/^\?/, "");
            return (
                pathname === itemPath &&
                currentSearch.includes(itemSearch || "")
            );
        }
        if (item.path && pathname === item.path) return true;
        // For list links without a querystring, exclude any path where the
        // page has a querystring filter that belongs to a different sidebar item.
        if (item.path && pathname?.startsWith(item.path)) {
            return true;
        }
        return false;
    };

    return (
        <>
            {/* <!-- Sidebar --> */}
            <div
                className={`sidebar ${isSidebarExpanded ? "" : "hidden"}`}
                id="sidebar"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Scrollbars
                    autoHide
                    autoHideTimeout={1000}
                    autoHideDuration={200}
                    autoHeight
                    autoHeightMin={0}
                    autoHeightMax="95vh"
                    thumbMinSize={30}
                    universal={false}
                    hideTracksWhenNotNeeded={true}
                >
                    <div className="sidebar-inner slimscroll">
                        <div id="sidebar-menu" className="sidebar-menu">
                            <ul>
                                {visibleMenuItems.map((item) => (
                                    <li
                                        key={item.path}
                                        className={
                                            isActive(item) ? "active" : ""
                                        }
                                    >
                                        <Link to={item.path}>
                                            <FaUserTie />{" "}
                                            <span>{item.label}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Scrollbars>
            </div>
            {/* <!-- /Sidebar --> */}
        </>
    );
};

export default SidebarNav;
